import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileQuestion, Zap, Loader2, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIQuestionGenerator = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subject: '',
    topic: '',
    difficulty: 'medium',
    num_questions: 5
  });
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState(null);

  const subjects = ['Quantitative Aptitude', 'Reasoning', 'English Language', 'General Awareness', 'Banking Awareness', 'Current Affairs'];

  useEffect(() => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      navigate('/student/login');
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject || !formData.topic) {
      alert('Please fill all required fields');
      return;
    }

    setLoading(true);
    setQuestions(null);

    try {
      const token = localStorage.getItem('studentToken');
      const response = await fetch(`${API_URL}/api/ai/generate-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to generate questions');
      }

      setQuestions(data.questions);
    } catch (error) {
      console.error('Question generation error:', error);
      alert('Failed to generate questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getOptionLabel = (index) => {
    return String.fromCharCode(65 + index); // A, B, C, D
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <FileQuestion className="mx-auto mb-4" size={48} />
          <h1 className="text-4xl font-bold mb-2">AI Question Generator</h1>
          <p className="text-red-100 text-lg">Generate practice questions instantly with AI</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Form Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-xl">
                    <Zap className="mr-2 text-red-600" size={20} />
                    Generate Questions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                        required
                      >
                        <option value="">Select subject</option>
                        {subjects.map(sub => (
                          <option key={sub} value={sub}>{sub}</option>
                        ))}
                      </select>
                    </div>

                    {/* Topic */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Topic <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="topic"
                        value={formData.topic}
                        onChange={handleInputChange}
                        placeholder="e.g., Profit & Loss, Data Interpretation"
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                        required
                      />
                    </div>

                    {/* Difficulty */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Difficulty Level
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {['easy', 'medium', 'hard'].map(level => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, difficulty: level }))}
                            className={`py-2 px-4 rounded-md border-2 font-medium capitalize ${
                              formData.difficulty === level
                                ? 'border-red-600 bg-red-50 text-red-700'
                                : 'border-gray-300 hover:border-red-400'
                            }`}
                          >
                            {level}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Number of Questions */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of Questions
                      </label>
                      <select
                        name="num_questions"
                        value={formData.num_questions}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                      >
                        {[3, 5, 10, 15].map(num => (
                          <option key={num} value={num}>{num} Questions</option>
                        ))}
                      </select>
                    </div>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-red-600 hover:bg-red-700 py-6 text-lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 animate-spin" size={20} />
                          Generating Questions...
                        </>
                      ) : (
                        <>
                          <Zap className="mr-2" size={20} />
                          Generate Questions
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Questions Display */}
              <div>
                {questions && questions.length > 0 && (
                  <Card className="border-2 border-red-500">
                    <CardHeader className="bg-red-50">
                      <CardTitle className="flex items-center text-xl text-red-700">
                        <FileQuestion className="mr-2" size={20} />
                        Generated Questions ({questions.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-6 max-h-[700px] overflow-y-auto">
                      {questions.map((q, qIdx) => (
                        <div key={qIdx} className="p-4 border rounded-lg bg-white">
                          <p className="font-semibold text-gray-900 mb-3">
                            Q{qIdx + 1}. {q.question}
                          </p>
                          
                          <div className="space-y-2 mb-4">
                            {q.options?.map((option, optIdx) => (
                              <div
                                key={optIdx}
                                className={`p-3 rounded-md border ${
                                  optIdx === q.correct_answer
                                    ? 'bg-green-50 border-green-500'
                                    : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                <div className="flex items-center">
                                  {optIdx === q.correct_answer && (
                                    <CheckCircle className="mr-2 text-green-600" size={16} />
                                  )}
                                  <span className="font-medium mr-2">{getOptionLabel(optIdx)}.</span>
                                  <span>{option}</span>
                                </div>
                              </div>
                            ))}
                          </div>

                          {q.explanation && (
                            <div className="p-3 bg-red-50 rounded-md">
                              <p className="text-xs font-semibold text-red-900 mb-1">Explanation:</p>
                              <p className="text-sm text-gray-700">{q.explanation}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {questions && questions.length === 0 && (
                  <Card>
                    <CardContent className="text-center py-12">
                      <XCircle className="mx-auto text-gray-400 mb-4" size={48} />
                      <p className="text-gray-600">No questions generated. Please try again.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AIQuestionGenerator;
